import { useState } from 'react';
import { Bookmark } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';
import { useSaveSearch } from '@/hooks/useSavedSearches';
import { PropertyFilters } from '@/types/property';
import { useNavigate } from 'react-router-dom';

interface SaveSearchDialogProps {
  filters: PropertyFilters;
  disabled?: boolean;
}

export function SaveSearchDialog({ filters, disabled }: SaveSearchDialogProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [emailAlerts, setEmailAlerts] = useState(false);
  const saveSearch = useSaveSearch();

  const hasFilters = Object.values(filters).some(v => v !== undefined);

  const handleSave = async () => {
    if (!name.trim()) return;
    
    await saveSearch.mutateAsync({
      name: name.trim(),
      filters,
      emailAlerts,
    });
    
    setOpen(false);
    setName('');
    setEmailAlerts(false);
  };

  const handleClick = () => {
    if (!user) {
      navigate('/auth?redirect=/search');
      return;
    }
    setOpen(true);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button 
          variant="outline" 
          className="gap-2" 
          disabled={disabled || !hasFilters}
          onClick={handleClick}
        >
          <Bookmark className="w-4 h-4" />
          Save Search
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Save This Search</DialogTitle>
          <DialogDescription>
            Give your search a name to easily find it later in your dashboard.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="search-name">Search Name</Label>
            <Input
              id="search-name"
              placeholder="e.g., Downtown condos under $500K"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSave()}
            />
          </div>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="email-alerts">Email Alerts</Label>
              <p className="text-sm text-muted-foreground">
                Get notified when new properties match
              </p>
            </div>
            <Switch
              id="email-alerts"
              checked={emailAlerts}
              onCheckedChange={setEmailAlerts}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={!name.trim() || saveSearch.isPending}
          >
            {saveSearch.isPending ? 'Saving...' : 'Save Search'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
